import { resolveApiBaseUrl } from "./apiBase";

const API_BASE_URL = resolveApiBaseUrl();

export type TenantListItem = {
  uid: string;
  name: string;
  abbreviation?: string;
};

const TENANTS_PATHS = ["/api/tenants", "/tenants"] as const;

/**
 * List active tenants (same contract as Tenants admin and user admin screens).
 */
export async function fetchTenantList(options?: {
  search?: string;
  token?: string | null;
}): Promise<TenantListItem[]> {
  const q = (options?.search ?? "").trim();
  const headers: Record<string, string> = {};
  if (options?.token) {
    headers.Authorization = `Bearer ${options.token}`;
  }

  let lastErr: Error | null = null;

  for (const path of TENANTS_PATHS) {
    try {
      const url = new URL(`${API_BASE_URL}${path}`);
      if (q) {
        url.searchParams.set("search", q);
      }
      const response = await fetch(url.toString(), { headers });
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        if (response.status === 404) {
          lastErr = new Error(data?.message || "Endpoint not found");
          continue;
        }
        throw new Error(data?.message || `Failed to load tenants (HTTP ${response.status})`);
      }

      if (!data?.success || !Array.isArray(data.tenants)) {
        return [];
      }

      return data.tenants
        .map((t: Record<string, unknown>) => ({
          uid: String(t.uid || t.id || ""),
          name: typeof t.name === "string" ? t.name : "",
          abbreviation: typeof t.abbreviation === "string" ? t.abbreviation : undefined,
        }))
        .filter((t: TenantListItem) => Boolean(t.uid && t.name));
    } catch (err) {
      lastErr = err instanceof Error ? err : new Error(String(err));
      const msg = lastErr.message || "";
      if (msg.includes("404") || msg.includes("Endpoint not found") || msg.includes("Not found")) {
        continue;
      }
      throw lastErr;
    }
  }

  throw lastErr ?? new Error("Failed to load tenants");
}
